import React, { useCallback, useContext, useRef } from 'react';
import {
  arrayOf, bool, func, number, string
} from 'prop-types';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { extend, QueryProvider, useDataModel } from '@thd-nucleus/data-sources';
import { Carousel } from '@thd-olt-component-react/carousel';
import { ProductPod } from '@thd-olt-component-react/product-pod';
import { Col, Row } from '@thd-olt-component-react/grid';
import className from 'classnames';
import { dataModel } from './dataModel';
import { ContainerHotspots } from './complete-the-look/ContainerHotspots';
import '../styles/thd-recs-containers.scss';
import { LoadingPlaceholder } from './LoadingPlaceholder';
import { getCollectionDetails } from './core/utils';

// the QueryProvider on line 54 doesnt get access to ProductPod because data?.products is empty at first
// this is what hoist() was originally intended for but the problem with hoist() is that it mutates
// components and runs into issues when they are in multiple places
// eslint-disable-next-line react/prop-types
const Wrapper = ({ children }) => children;
Wrapper.dataModel = extend({}, ProductPod);

const CompleteTheLookProductPodCarousel = (props) => {

  const { storeId, storeZip: zipCode, isLocalized } = useStore();
  const { channel } = useContext(ExperienceContext);
  const loadedStoreInfo = useRef({ storeId, zipCode });

  const isMobile = channel === 'mobile';

  const {
    axiosLoading,
    scheme,
    showLoading,
    skus,
    title,
    collectionUrl,
    requestKey,

    // carouselProps,
    fixedItem,
    itemClass,
    itemWidthPixelsDesktop,
    itemWidthPixelsMobile,
    multiItem,
    onClickItem,
    showArrows,

    // productPodProps,
    products,
    hideATC,
    hideFulfillment,
    hideLimitPerOrder,
    hidePriceBadge,
    hideRebates,
    hideSavingsText,
    hideShippingInfo,
    hideShopThisCollection,
    loadingLinesCount,
    loadingImageHeight
  } = props;

  const opts = {
    variables: {
      itemIds: skus,
      storeId,
      zipCode,
      dataSource: scheme,
    },
    skip: !isLocalized || skus.length === 0,
    ssr: false
  };

  const { data, variables, loading, error } = useDataModel('recsProducts', opts);

  const { hotspots, collectionImage } = getCollectionDetails(products, scheme);

  const renderCollectionUrl = () => {
    if (!(scheme === 'ctl' && collectionUrl)) return null;

    return (
      <div className="row-header__link collection__redirect">
        <a href={collectionUrl}>
          View This Collection
        </a>
      </div>
    );
  };

  if (data && !loading && isLocalized) {
    loadedStoreInfo.current = {
      storeId: variables.storeId,
      zipCode: variables.zipCode,
      mounted: true
    };
  }

  // mounted is when you want it to fetch client() only fields
  // such  as fulfillment. You want to use this flag
  // for inner queries to not change to localized values
  // storeId, zip, until the outer query has resolved
  const mountedFn = useCallback(
    () => loadedStoreInfo.current.mounted,
    [loadedStoreInfo.current.mounted]
  );

  const defaultVariables = {
    storeId: loadedStoreInfo.current.storeId,
    zipCode: loadedStoreInfo.current.zipCode
  };
  const wrapperClasses = className(
    'dynamic-recs',
    scheme,
    {
      loading: showLoading && axiosLoading,
      'ctl--mobile': isMobile,
    }
  );

  if (!(data?.products || data?.products?.length > 0) && !axiosLoading) return null;

  return (
    <Row className={wrapperClasses} data-testid="ctl-container">
      <QueryProvider
        dataSource={scheme}
        cacheKey="recs-product-pods"
        mounted={mountedFn}
        defaultVariables={defaultVariables}
      >
        <Wrapper>
          {showLoading && (axiosLoading || loading) && (
            <LoadingPlaceholder
              scheme={scheme}
              itemClass={itemClass}
              fixedItem={fixedItem}
              multiItem={multiItem}
              itemWidthPixelsDesktop={itemWidthPixelsDesktop}
              itemWidthPixelsMobile={itemWidthPixelsMobile}
              loadingLinesCount={loadingLinesCount}
              loadingImageHeight={loadingImageHeight}
            />
          )}
          {data?.products?.length > 0 && (
            <>
              <Col className="header-container">
                <div className="row-header">
                  <div className="row-header__title">
                    <h2 className="row-header__text">{title}</h2>
                  </div>
                  <div className="collection-header">
                    <div className="row-header__link shop-by-room__redirect">
                      <a href="https://www.homedepot.com/room/" target="_blank" rel="noreferrer">Explore
                        More Rooms
                      </a>
                    </div>
                    {!isMobile && renderCollectionUrl()}
                  </div>
                </div>
              </Col>
              {collectionImage && (
                <Col xs={4} className="ctl--centered">
                  <ContainerHotspots
                    requestKey={requestKey}
                    storeId={storeId}
                    channel={channel}
                    hotspots={hotspots}
                    products={data?.products}
                    skus={skus}
                    scheme={scheme}
                    collectionImage={collectionImage}
                  />
                </Col>
              )}
              <Col xs="8" className="carousel__container" data-testid="carousel-container">
                {isMobile && (
                  <div className="row-header collection-header">
                    <div className="row-header__title">
                      <span className="row-header__text carousel-header">Items In This Room</span>
                    </div>
                    {renderCollectionUrl()}
                  </div>
                )}
                <Carousel
                  name={scheme}
                  id={scheme}
                  itemClass={itemClass}
                  fixedItem={fixedItem}
                  multiItem={multiItem}
                  itemWidthPixelsDesktop={itemWidthPixelsDesktop}
                  itemWidthPixelsMobile={itemWidthPixelsMobile}
                  showArrows={showArrows}
                  totalItems={data?.products.length}
                  onClickItem={onClickItem || null}
                >
                  {data?.products.map((product, idx) => {
                    const { itemId } = product;
                    return (
                      <ProductPod
                        key={itemId}
                        itemId={itemId}
                        hideATC={hideATC}
                        hideFulfillment={hideFulfillment}
                        hideLimitPerOrder={hideLimitPerOrder}
                        hidePriceBadge={hidePriceBadge}
                        hideRebates={hideRebates}
                        hideSavingsText={hideSavingsText}
                        hideShopThisCollection={hideShopThisCollection}
                        hideShippingInfo={hideShippingInfo}
                        storeId={storeId}
                        position={idx}
                      />
                    );
                  }
                  )}
                </Carousel>
              </Col>
            </>
          )}
        </Wrapper>
      </QueryProvider>
    </Row>
  );
};

CompleteTheLookProductPodCarousel.displayName = 'CompleteTheLookProductPodCarousel';

CompleteTheLookProductPodCarousel.dataModel = dataModel;

CompleteTheLookProductPodCarousel.propTypes = {
  ...Carousel.propTypes,
  ...ProductPod.propTypes,
  axiosLoading: bool,
  collectionUrl: string,
  scheme: string,
  skus: arrayOf(string),
  showLoading: bool,
  title: string,

  // carouselProps
  fixedItem: bool,
  itemClass: string,
  itemWidthPixelsDesktop: number,
  itemWidthPixelsMobile: number,
  multiItem: bool,
  onClickItem: func, // requires ( index: number, item: any ),

  // productPodProps
  hideATC: bool,
  hideFulfillment: bool,
  hideLimitPerOrder: bool,
  hidePriceBadge: bool,
  hideRebates: bool,
  hideSavingsText: bool,
  hideShopThisCollection: bool,
  hideShippingInfo: bool,
  loadingImageHeight: number,
  loadingLinesCount: number,
};

CompleteTheLookProductPodCarousel.defaultProps = {
  axiosLoading: false,
  collectionUrl: null,
  scheme: 'drecs',
  skus: [],
  showLoading: false,
  title: '',

  fixedItem: true,
  itemClass: '',
  itemWidthPixelsDesktop: 220,
  itemWidthPixelsMobile: 150,
  multiItem: false,
  onClickItem: null,

  hideATC: false,
  hideFulfillment: true,
  hideLimitPerOrder: true,
  hidePriceBadge: true,
  hideRebates: true,
  hideSavingsText: true,
  hideShopThisCollection: true,
  hideShippingInfo: true,
  loadingImageHeight: null,
  loadingLinesCount: 5
};

export { CompleteTheLookProductPodCarousel };
